import React, { useRef, forwardRef, useImperativeHandle, useEffect, useCallback, useState } from 'react';
import lodashThrottle from 'lodash.throttle';
import { getScrollContainerAttribute, getValidScrollContainer, defaultLocale } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import { useUpdateEffect } from '../_helpers';

/**
 * 上拉加载组件，支持`scroll`和`click`两种触发加载方式，支持滚动监听。支持受控与不受控两种形式。<br>如果引入组件后发现仅触发了初始的`getData`，请确认是否在`getData`方法内没有调用`callback`移除 loading 状态，且未设置`blockWhenLoading`属性为 false。
 * @en Pull-up loading component, supports `scroll` and `click` two trigger loading methods, supports scroll monitoring. Both controlled and uncontrolled forms are supported. <br>If only the initial `getData` is triggered after the component is introduced, please make sure that the `callback` is not called in the `getData` method to remove the loading state, and the `blockWhenLoading` property is not set to false.
 * @type 反馈
 * @type_en Feedback
 * @name 加载更多
 * @name_en LoadMore
 */
var LoadMore = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      style = props.style,
      beforeReadyArea = props.beforeReadyArea,
      loadingArea = props.loadingArea,
      noMoreArea = props.noMoreArea,
      prepareArea = props.prepareArea,
      retryArea = props.retryArea,
      _props$defaultStatus = props.defaultStatus,
      defaultStatus = _props$defaultStatus === void 0 ? 'prepare' : _props$defaultStatus,
      status = props.status,
      getScrollContainer = props.getScrollContainer,
      _props$trigger = props.trigger,
      trigger = _props$trigger === void 0 ? 'scroll' : _props$trigger,
      _props$threshold = props.threshold,
      threshold = _props$threshold === void 0 ? 200 : _props$threshold,
      _props$throttle = props.throttle,
      throttle = _props$throttle === void 0 ? 0 : _props$throttle,
      _props$getDataAtFirst = props.getDataAtFirst,
      getDataAtFirst = _props$getDataAtFirst === void 0 ? true : _props$getDataAtFirst,
      getData = props.getData,
      _props$blockWhenLoadi = props.blockWhenLoading,
      blockWhenLoading = _props$blockWhenLoadi === void 0 ? true : _props$blockWhenLoadi,
      onStatusChange = props.onStatusChange,
      onClick = props.onClick,
      onEndReached = props.onEndReached;
  var domRef = useRef(null);
  var requestAtFirst = trigger === 'scroll' ? getDataAtFirst : false;

  var _useState = useState(defaultStatus),
      innerStatus = _useState[0],
      setInnerStatus = _useState[1];

  var lastScrollEndRef = useRef(false);
  var nowStatus = status || innerStatus;
  var statusRef = useRef(nowStatus);
  var changeStatus = useCallback(function (st, scene) {
    setInnerStatus(st);
    onStatusChange && onStatusChange(st, scene);
  }, [onStatusChange]);
  var triggerGetData = useCallback(function (scene) {
    if (blockWhenLoading && statusRef.current === 'loading') {
      return;
    }

    changeStatus('loading', scene);
    getData == null ? void 0 : getData(function (st) {
      lastScrollEndRef.current = false;
      changeStatus(st, 'manual');
    });
  }, [blockWhenLoading, changeStatus, getData]);
  useEffect(function () {
    statusRef.current = nowStatus;
  }, [nowStatus]);
  useUpdateEffect(function () {
    // 当状态被设置为 prepare ，且页面总长度不足一屏时，再触发一次 getData
    // @en When the state is set to prepare and the total page length is less than one screen, trigger getData again
    if (trigger === 'scroll' && nowStatus === 'prepare' && checkNeedTrigger(0, threshold)) {
      triggerGetData('pageEnd');
    }
  }, [nowStatus]);
  useEffect(function () {
    if (requestAtFirst) {
      if (statusRef.current === 'prepare') {
        triggerGetData('requestAtFirst');
      }
    }
  }, [trigger]);
  var handleContainerScroll = useCallback(function () {
    var scrollTop = getScrollContainerAttribute('scrollTop', getScrollContainer);

    if (checkNeedTrigger(scrollTop, threshold + 1)) {
      if (!lastScrollEndRef.current) {
        lastScrollEndRef.current = true;
        onEndReached && onEndReached();

        if (!['nomore', 'retry', 'before-ready'].includes(statusRef.current)) {
          triggerGetData('scrollEnd');
        }
      }
    } else {
      lastScrollEndRef.current = false;
    }
  }, [getScrollContainer, threshold, triggerGetData, onEndReached]);
  useEffect(function () {
    var binded = null;
    var scrollFunc = throttle ? lodashThrottle(handleContainerScroll, throttle) : handleContainerScroll;

    if (trigger === 'scroll') {
      var container = getValidScrollContainer(getScrollContainer);

      if (container) {
        container.addEventListener('scroll', scrollFunc);
        binded = container;
      }
    }

    return function () {
      if (binded) {
        binded.removeEventListener('scroll', scrollFunc);
      }
    };
  }, [trigger, getScrollContainer, handleContainerScroll, throttle]);
  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current,
      changeStatus: changeStatus
    };
  }, [changeStatus]);

  function checkNeedTrigger(top, ths) {
    var scrollHeight = getScrollContainerAttribute('scrollHeight', getScrollContainer);
    var clientHeight = getScrollContainerAttribute('clientHeight', getScrollContainer);
    return scrollHeight - top - clientHeight <= ths;
  }

  function handleClick(e) {
    if (trigger === 'click' && statusRef.current === 'prepare' || statusRef.current === 'retry') {
      triggerGetData('click');
    }

    onClick && onClick(e);
  }

  function renderArea(locale) {
    switch (nowStatus) {
      case 'before-ready':
        return beforeReadyArea;

      case 'prepare':
        return prepareArea === void 0 ? /*#__PURE__*/React.createElement("div", {
          className: "load-more-text prepare"
        }, trigger === 'scroll' ? locale.LoadMore.prepareScrollText : locale.LoadMore.prepareClickText, locale.LoadMore.loadMoreText) : prepareArea;

      case 'loading':
        return loadingArea === void 0 ? /*#__PURE__*/React.createElement("div", {
          className: "load-more-text loading"
        }, locale.LoadMore.loadingText) : loadingArea;

      case 'nomore':
        return noMoreArea === void 0 ? /*#__PURE__*/React.createElement("div", {
          className: "load-more-text nomore"
        }, locale.LoadMore.noDataText) : noMoreArea;

      case 'retry':
        return retryArea === void 0 ? /*#__PURE__*/React.createElement("div", {
          className: "load-more-text retry"
        }, locale.LoadMore.failLoadText) : retryArea;

      default:
        return null;
    }
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls,
        _ref$locale = _ref.locale,
        locale = _ref$locale === void 0 ? defaultLocale : _ref$locale;
    return /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-load-more status-" + nowStatus + " " + className,
      ref: domRef,
      style: style,
      onClick: handleClick
    }, renderArea(locale));
  });
});
export default LoadMore;